/**
 * SankeyRenderer — two-level flow viz (source → target with value).
 *
 * First use case: agency → vendor parent (after Phase M.I2 backend lands the
 * `?rollup=parent` mart column). Second: PSC L1 → vehicle category.
 *
 * Expects either:
 *   - flat rows with `sourceField` / `targetField` / metric; the renderer
 *     pivots to nodes + links, OR
 *   - pre-built `nodes` + `links` arrays (advanced caller)
 *
 * Phase C7 of issue #90 v3 plan.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey } from './types'
import { METRIC_LABELS, formatMetric } from './types'
import { useExecChartTheme } from './echarts-theme'

interface SankeyNode { name: string }
interface SankeyLink { source: string; target: string; value: number }

interface Props {
  data: PerfRow[]
  metrics: MetricKey[]
  /** Source column name on each row (e.g., 'sub_agency_code'). */
  sourceField?: string
  /** Target column name on each row (e.g., 'recipient_parent_uei'). */
  targetField?: string
  /** Optional pre-built nodes + links (overrides flat conversion). */
  nodes?: SankeyNode[]
  links?: SankeyLink[]
  /** Limit nodes per side (top-N by total flow) to keep the chart legible. */
  topN?: number
}

export default function SankeyRenderer({
  data, metrics, sourceField, targetField, nodes, links, topN = 12,
}: Props) {
  const theme = useExecChartTheme()
  const metric = metrics[0]

  const { effectiveNodes, effectiveLinks } = useMemo(() => {
    if (nodes && links) {
      return { effectiveNodes: nodes, effectiveLinks: links }
    }
    if (!sourceField || !targetField) {
      return { effectiveNodes: [] as SankeyNode[], effectiveLinks: [] as SankeyLink[] }
    }

    // Pivot flat rows → nodes/links
    const flow = new Map<string, number>() // "src→tgt" → sum
    const sourceTotal = new Map<string, number>()
    const targetTotal = new Map<string, number>()
    for (const row of data) {
      const s = String(row[sourceField] ?? '')
      const t = String(row[targetField] ?? '')
      if (!s || !t) continue
      const v = Number(row[metric])
      if (!Number.isFinite(v) || v <= 0) continue
      flow.set(`${s}→${t}`, (flow.get(`${s}→${t}`) || 0) + v)
      sourceTotal.set(s, (sourceTotal.get(s) || 0) + v)
      targetTotal.set(t, (targetTotal.get(t) || 0) + v)
    }

    // Keep only the top-N sources and targets by total flow
    const topSources = new Set(
      [...sourceTotal.entries()].sort((a, b) => b[1] - a[1]).slice(0, topN).map(([k]) => k),
    )
    const topTargets = new Set(
      [...targetTotal.entries()].sort((a, b) => b[1] - a[1]).slice(0, topN).map(([k]) => k),
    )

    const nodeNames = new Set<string>()
    const filteredLinks: SankeyLink[] = []
    for (const [k, v] of flow) {
      const [s, t] = k.split('→')
      if (!topSources.has(s) || !topTargets.has(t)) continue
      filteredLinks.push({ source: s, target: t, value: v })
      nodeNames.add(s)
      nodeNames.add(t)
    }
    const nodeArr: SankeyNode[] = [...nodeNames].map((n) => ({ name: n }))
    return { effectiveNodes: nodeArr, effectiveLinks: filteredLinks }
  }, [data, metric, sourceField, targetField, nodes, links, topN])

  if (effectiveNodes.length === 0 || effectiveLinks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No flow data
      </div>
    )
  }

  const option: any = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.dataType === 'edge') {
          return `${params.data.source} → ${params.data.target}<br/>${METRIC_LABELS[metric]}: <b>${formatMetric(metric, params.data.value)}</b>`
        }
        return `<b>${params.name}</b>`
      },
    },
    series: [
      {
        type: 'sankey',
        data: effectiveNodes,
        links: effectiveLinks,
        emphasis: { focus: 'adjacency' },
        label: { fontSize: 10 },
        lineStyle: { color: 'gradient', curveness: 0.5 },
      },
    ],
  }

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  )
}
